// Shared store list. Both AboutPageTab (admin > Pages > About) and the
// Shipping & pickup panel (admin > Processing > Shipping) read from this
// module so they're guaranteed to show the same set of stores. State
// hoisting (cross-tab edits) is a follow-up; for now both tabs maintain
// their own local copies but share the same defaults — making sure
// pickup toggles keyed by store id stay valid even when the user edits a
// store's display fields elsewhere.

const defaultDayHours = (open = true) => ({
    open,
    ranges: open ? [{ start: '09:30', end: '18:30' }] : [],
});
const defaultOpeningHours = () => ({
    monday:    defaultDayHours(true),
    tuesday:   defaultDayHours(true),
    wednesday: defaultDayHours(true),
    thursday:  defaultDayHours(true),
    friday:    defaultDayHours(true),
    saturday:  defaultDayHours(true),
    sunday:    defaultDayHours(false),
});

export const DEFAULT_STORES = [
    { id: 'lochristi',    name: 'Lochristi',    street: 'Ambachtenlaan 6A',         zip: '9080', city: 'Lochristi 2',  country: 'BE', lat: '51.1058',  lng: '3.87373', phone: '+32 9 219 00 83', email: 'supportl@softtouch.be', visible: true,  remark: { nl: 'Gesloten op zon- en feestdagen. Iedere eerste zondag van de maand uitzonderlijk geopend van 13:00 tot 17:00.', en: '', fr: '' } },
    { id: 'charleroi',    name: 'Charleroi',    street: 'Boulevard Tirou 45',       zip: '6000', city: 'Charleroi',    country: 'BE', visible: true  },
    { id: 'destelbergen', name: 'Destelbergen', street: 'Dendermondesteenweg 156',  zip: '9070', city: 'Destelbergen', country: 'BE', visible: true  },
    { id: 'softtouch',    name: 'softtouch',    street: 'Kerkstraat 4',             zip: '9080', city: 'Lochristi',    country: 'BE', visible: true  },
    { id: 'kuurne',       name: 'Kuurne',       street: 'Kortrijksestraat 22',      zip: '8520', city: 'Kuurne',       country: 'BE', visible: true  },
    { id: 'gavere',       name: 'Gavere',       street: 'Markt 17',                 zip: '9890', city: 'Gavere',       country: 'BE', visible: true  },
    { id: 'sdw',          name: 'SDW',          street: 'Industriepark 9',          zip: '9550', city: 'SDW',          country: 'BE', visible: true  },
    { id: 'ternat',       name: 'Ternat',       street: 'Brusselstraat 77',         zip: '1740', city: 'Ternat',       country: 'BE', visible: false },
    { id: 'ontvangst',    name: 'Ontvangst',    street: 'Kerkstraat 1',             zip: '9080', city: 'Lochristi',    country: 'BE', visible: false },
].map(s => ({
    lat: '', lng: '', phone: '', email: '',
    remark: { nl: '', en: '', fr: '' },
    openingHours: defaultOpeningHours(),
    allowPickup: true,
    ...s,
}));

// ───────────────────────────────────────────────────────────────────────────
// Backend mapping. The mini-api `store` table keeps opening hours as one
// free-text string per day (hours_mon..hours_sun, e.g. "09:30 - 18:30" or
// "09:30 - 12:00, 13:00 - 18:30"), the address in `address` (not `street`),
// the customer-facing name in `display_name`, lat/lng as floats and remarks in
// remark_{nl,en,fr}. These helpers convert between that row shape and the
// structured form the admin editor works with so load/save round-trips
// cleanly. Shared by AboutPageTab (classic hours editor) and the save flow in
// WebshopConfigScreen.
// ───────────────────────────────────────────────────────────────────────────
export const DAY_TO_COL = {
    monday: 'hours_mon', tuesday: 'hours_tue', wednesday: 'hours_wed',
    thursday: 'hours_thu', friday: 'hours_fri', saturday: 'hours_sat', sunday: 'hours_sun',
};

// Tolerant time parser — accepts "9:30", "09:30", "9u30", "9.30", "18u", "930".
export const normalizeTime = (s) => {
    const t = String(s).trim().toLowerCase().replace(/[hu.]/g, ':');
    let m = t.match(/^(\d{1,2})(?::(\d{1,2}))?:?$/);
    if (!m) {
        const compact = t.match(/^(\d{3,4})$/);
        if (!compact) return null;
        const padded = compact[1].padStart(4, '0');
        m = [null, padded.slice(0, 2), padded.slice(2)];
    }
    const hh = Number(m[1]);
    const mm = m[2] != null && m[2] !== '' ? Number(m[2]) : 0;
    if (Number.isNaN(hh) || Number.isNaN(mm) || hh > 23 || mm > 59) return null;
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
};

// The separator between two ranges (a lunch break) is a per-customer style
// choice that lives inside the stored string — some use a comma, others " en ",
// "&", "/", … There is no backend setting for it, so the admin picks a
// separator, writes it into the strings, and re-detects it on load. Parsing is
// separator-agnostic (it extracts the time tokens directly) so any style reads
// back correctly.
export const DEFAULT_HOURS_SEPARATOR = ', ';
export const HOURS_SEPARATOR_PRESETS = [', ', ' en ', ' & ', ' / '];

// One side of a range: "9", "09:30", "9u30", "9.30", "930", "0930".
const RANGE_SIDE = '\\d{1,2}(?:[:.uh]?\\d{2})?';
const RANGE_PATTERN = `(${RANGE_SIDE})\\s*[-–—~]\\s*(${RANGE_SIDE})`;

// {open, ranges:[{start,end}]} → "09:30 - 18:30" (empty string when closed).
// `sep` joins multiple ranges (the break separator).
export const formatRanges = (value, sep = DEFAULT_HOURS_SEPARATOR) => {
    if (!value?.open || !(value.ranges || []).length) return '';
    return value.ranges.map(r => `${r.start} - ${r.end}`).join(sep);
};

// "09:30 - 12:00 en 13:00 - 18:30" → {open, ranges:[{start,end}]}. Extracts the
// "HH:MM - HH:MM" tokens regardless of which separator joins them, so it copes
// with comma, " en ", "&", "/", custom, … (empty / no ranges = closed).
export const parseRanges = (text) => {
    const ranges = [];
    const re = new RegExp(RANGE_PATTERN, 'g');
    let m;
    while ((m = re.exec(String(text))) !== null) {
        const start = normalizeTime(m[1]);
        const end = normalizeTime(m[2]);
        if (start && end) ranges.push({ start, end });
    }
    return ranges.length ? { open: true, ranges } : { open: false, ranges: [] };
};

// Recover the break separator from already-saved hours strings by capturing the
// literal text sitting between the first two ranges (incl. its spaces). Returns
// `fallback` when no store has a break yet. Works for custom separators too.
export const detectHoursSeparator = (rows, fallback = DEFAULT_HOURS_SEPARATOR) => {
    const re = new RegExp(`${RANGE_PATTERN}(.+?)${RANGE_PATTERN}`);
    for (const row of rows || []) {
        for (const col of Object.values(DAY_TO_COL)) {
            const m = String(row?.[col] || '').match(re);
            // groups: 1,2 = first range sides · 3 = separator · 4,5 = second range
            if (m && m[3]) return m[3];
        }
    }
    return fallback;
};

// Backend `store` row → the admin editor's store shape.
export const storeRowToForm = (row) => ({
    id:      String(row.id),
    name:    row.display_name || row.name || '',
    street:  row.address || '',
    zip:     row.zip || '',
    city:    row.city || '',
    country: row.country || 'BE',
    lat:     row.lat != null && row.lat !== '' ? String(row.lat) : '',
    lng:     row.lng != null && row.lng !== '' ? String(row.lng) : '',
    phone:   row.phone || '',
    email:   row.email || '',
    visible: !!Number(row.visible),
    // allow_pickup column (mig169) — default true when the column is absent.
    allowPickup: row.allow_pickup != null ? !!Number(row.allow_pickup) : true,
    // Per-store pickup POS ids (legacy admin data-manager: pos_id / pos_id_print).
    // posId       = "register order on this POS (pickup)" (STORE_POS_ID_PROCESS)
    // posIdPrint  = "print order on this POS (pickup)"    (STORE_POS_ID_PRINT)
    posId:      row.pos_id       != null ? String(row.pos_id)       : '',
    posIdPrint: row.pos_id_print != null ? String(row.pos_id_print) : '',
    remark:  { nl: row.remark_nl || '', en: row.remark_en || '', fr: row.remark_fr || '' },
    openingHours: Object.fromEntries(
        Object.entries(DAY_TO_COL).map(([day, col]) => [day, parseRanges(row[col] || '')]),
    ),
});

// Admin store shape → the backend column map (the full set; callers diff this
// against the original to PATCH only changed columns). `name` is written to
// both `name` and `display_name` to match the legacy admin, which keeps them
// in sync. Empty lat/lng become null so the DB clears them rather than storing 0.
export const storeFormToColumns = (s, sep = DEFAULT_HOURS_SEPARATOR) => ({
    display_name: s.name || '',
    name:         s.name || '',
    address:      s.street || '',
    zip:          s.zip || '',
    city:         s.city || '',
    country:      s.country || '',
    phone:        s.phone || '',
    email:        s.email || '',
    lat:          s.lat === '' || s.lat == null ? null : s.lat,
    lng:          s.lng === '' || s.lng == null ? null : s.lng,
    visible:      s.visible ? 1 : 0,
    allow_pickup: s.allowPickup === false ? 0 : 1,
    pos_id:       s.posId      || '',
    pos_id_print: s.posIdPrint || '',
    remark_nl:    s.remark?.nl || '',
    remark_en:    s.remark?.en || '',
    remark_fr:    s.remark?.fr || '',
    ...Object.fromEntries(
        Object.entries(DAY_TO_COL).map(([day, col]) => [col, formatRanges(s.openingHours?.[day], sep)]),
    ),
});
